import { Button, PageHeader, Spinner } from "@justworkshr/milo-core";
import {
  buildWebPath,
  useNeonPath,
  CLOCKFACE_PATHS,
} from "lib/resource-finder";
import Collapsible from "./collapsible";
import {
  useFirmsPageQuery,
  useGetCompanyDisplayQuery,
  useGetIsEmployerOfRecordEnabledQuery,
  useGetCompanyInfoQuery,
  FirmStatus,
  CompanyPlan,
} from "types/generated/operations";
import { FLAG_NAMES, flagIsEnabled } from "lib/environment";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router-dom";
import styles from "./invitation-center.module.css";
import { canManageFirms } from "pages/advisor/common/utils";
import Tools from "./Tools/Tools";
import { useGetFeatureFlag } from "lib/launch-darkly";

const { invitationCenter, collapsible, item, header, fullPageSpinner } = styles;

export default function International() {
  const { hash } = useLocation();
  const { t } = useTranslation();
  const employeesPath = useNeonPath(CLOCKFACE_PATHS.MEMBERS_EMPLOYEES);
  const firmsInvitationPath = useNeonPath(CLOCKFACE_PATHS.FIRMS_INVITATION_NEW);
  const { getFeatureFlag } = useGetFeatureFlag();
  const isSowInviteEnabled = getFeatureFlag("sow-invite-enabled");
  const legacyEorInvitationPath = "/employer-of-record/invite";
  const newEorInvitationPath = useNeonPath(
    CLOCKFACE_PATHS.EMPLOYER_OF_RECORD_INVITE
  );
  const eorInvitationPath = isSowInviteEnabled
    ? newEorInvitationPath
    : legacyEorInvitationPath;
  const { data: eorEnabledData, loading: eorEnabledLoading } =
    useGetIsEmployerOfRecordEnabledQuery();

  const {
    data: companyData,
    loading: companyLoading,
    error: companyError,
  } = useGetCompanyDisplayQuery();

  const companyUuid = companyData?.authenticatedMember?.company?.uuid ?? "";

  const { data: firmData, loading: firmLoading } = useFirmsPageQuery({
    skip: companyLoading || !!companyError,

    variables: { companyUuid },
  });
  const canManageFirm = canManageFirms(
    firmData?.authenticatedMember?.permissions
  );

  const { firm } = firmData || {};

  if (hash) {
    const element = document.querySelector(hash);
    if (element) {
      element.scrollIntoView();
    }
  }

  const { data: companyInfoData, loading: companyInfoLoading } =
    useGetCompanyInfoQuery();

  if (eorEnabledLoading || companyInfoLoading)
    return (
      <div className={fullPageSpinner}>
        <Spinner />
      </div>
    );

  const isEmployerOfRecordEnabled =
    eorEnabledData?.authenticatedMember.company.companySetting?.value ===
      "true" && flagIsEnabled(FLAG_NAMES.EMPLOYER_OF_RECORD);

  const canAddThirdPartyAccountant =
    canManageFirm && firm?.status !== FirmStatus.Active;

  const isInternationalStandalone =
    companyInfoData?.authenticatedMember?.company?.companyPlanType ===
    CompanyPlan.InternationalStandalone;

  return (
    <div id="InvitationCenterApp">
      <div className={`${invitationCenter} milo--grid`}>
        <PageHeader
          className={header}
          title="Invitation center"
          linkPrevious={<a href={employeesPath}>Back to manage</a>}
        />
        <Collapsible
          className={collapsible}
          title="Add employees"
          open={!hash || hash === "#employees"}
          id="employees"
        >
          {!isInternationalStandalone && (
            <div className={item}>
              <div>
                <h3>U.S. full-time (W-2)</h3>
                <p>
                  Someone who will be employed by your company on a full-time
                  basis and lives in the United States.
                </p>
              </div>
              <Button
                as="a"
                href={buildWebPath(
                  "/invitation?invitation_member_type=employee_full_time"
                )}
                variant="outlined"
              >
                Add employee
              </Button>
            </div>
          )}
          {!isInternationalStandalone && (
            <div className={item}>
              <div>
                <h3>U.S. part-time (W-2) </h3>
                <p>
                  Someone who will be employed by your company on a part-time
                  basis and lives in the United States.
                </p>
              </div>
              <Button
                as="a"
                href={buildWebPath(
                  "/invitation?invitation_member_type=employee_part_time"
                )}
                variant="outlined"
              >
                Add employee
              </Button>
            </div>
          )}
          {!isInternationalStandalone && (
            <div className={item}>
              <div>
                <h3>U.S. paid intern (W-2)</h3>
                <p>
                  A full-time or part-time, hourly, non-exempt employee who is
                  being paid at least the minimum wage rate, overtime pay for
                  overtime hours worked, and lives in the United States.
                </p>
              </div>
              <Button
                as="a"
                href={buildWebPath(
                  "/invitation?invitation_member_type=paid_intern"
                )}
                variant="outlined"
              >
                Add intern
              </Button>
            </div>
          )}
          {isEmployerOfRecordEnabled && (
            <div
              className={`${item} ${
                hash === "#employees" ? styles.intlFocus : ""
              }`}
            >
              <div>
                <h3>{t(`International employee (EOR)`)}</h3>
                <p>
                  {t(
                    "Use Justworks as an employer of record (EOR) to hire people outside of the United States on your behalf."
                  )}
                </p>
              </div>
              <Button as={Link} to={eorInvitationPath} variant="outlined">
                {t("Add employee")}
              </Button>
            </div>
          )}
        </Collapsible>
        <Collapsible
          className={collapsible}
          title="Add limited access employees"
          id="limited-access-employee"
          open={hash === "#limited-access-employee"}
        >
          <div className={item}>
            <div>
              <h3>Outside the U.S.</h3>
              <p>
                Someone living and/or working outside of the United States who
                will be able to access documents, the company directory,
                calendar, and time off. Justworks services do not apply to these
                employees. They will not be paid or receive benefits through
                Justworks.
              </p>
            </div>
            <Button
              as="a"
              href={buildWebPath(
                "/invitation?invitation_member_type=employee_international"
              )}
              variant="outlined"
            >
              Add employee
            </Button>
          </div>
        </Collapsible>
        <Collapsible
          className={collapsible}
          title="Add contractors or vendors"
          id="contractors-or-vendors"
          open={hash === "#contractors-or-vendors"}
        >
          {!isInternationalStandalone && (
            <div className={item}>
              <div>
                <h3>U.S. contractor or consultant (1099)</h3>
                <p>
                  Someone who provides services to your company, but is not
                  employed on an ongoing basis. They generally use their own
                  equipment, work on their own schedule, and lives in the United
                  States.
                </p>
              </div>
              <Button
                as="a"
                href={buildWebPath(
                  "/invitation?invitation_member_type=contractor"
                )}
                variant="outlined"
              >
                Add contractor
              </Button>
            </div>
          )}
          {!isInternationalStandalone && (
            <div className={item}>
              <div>
                <h3>U.S. vendor (1099)</h3>
                <p>
                  A company that provides goods or services to your company and
                  has their own EIN.
                </p>
              </div>
              <Button
                as="a"
                href={buildWebPath(
                  "/invitation?invitation_member_type=vendor_1099"
                )}
                variant="outlined"
              >
                Add vendor
              </Button>
            </div>
          )}
          <div
            className={`${item} ${
              hash === "#contractors-or-vendors" ? styles.intlFocus : ""
            }`}
          >
            <div>
              <h3>International Contractors</h3>{" "}
              <p>
                Someone who provides services to your company, but is not
                employed by your company. They generally use their own
                equipment, work on their own schedule, and live outside the
                United States.
              </p>
              <a
                href={buildWebPath("/international_contractors")}
                target="_blank"
                rel="noopener noreferrer"
              >
                Learn More
              </a>
            </div>
            <Button
              as="a"
              href={buildWebPath("/international/contractor/invite")}
              variant="outlined"
            >
              Add contractor
            </Button>
          </div>
        </Collapsible>
        {!isInternationalStandalone && (
          <Collapsible
            className={collapsible}
            title="Add owners or partners"
            id="owners-or-partners"
            open={hash === "#owners-or-partners"}
          >
            <div className={item}>
              <div>
                <h3>Paid</h3>
                <p>Owners or partners who will be included in payroll.</p>
              </div>
              <Button
                as="a"
                href={buildWebPath(
                  "/invitation?invitation_member_type=owner_full_time"
                )}
                variant="outlined"
              >
                Add owner
              </Button>
            </div>
            <div className={item}>
              <div>
                <h3>Unpaid</h3>
                <p>
                  Owners or partners who will not be included in your company's
                  payroll.
                </p>
              </div>
              <Button
                as="a"
                href={buildWebPath("/invitation?invitation_member_type=owner")}
                variant="outlined"
              >
                Add vendor
              </Button>
            </div>
          </Collapsible>
        )}
        <Collapsible
          className={collapsible}
          title="Add third party or temp"
          id="third-party-or-temp"
          open={hash === "#third-party-or-temp"}
        >
          {canAddThirdPartyAccountant ? (
            <>
              <div className={item}>
                <div>
                  <h3>Accountant or Accounting firm</h3>
                  <p>
                    When you need to grant access to an individual accountant or
                    an accounting firm with multiple team members.
                  </p>
                </div>
                <Button
                  disabled={firm?.status === FirmStatus.Pending}
                  loading={firmLoading}
                  as={Link}
                  to={firmsInvitationPath}
                  variant="outlined"
                >
                  {firm?.status === FirmStatus.Pending
                    ? "Invitation pending"
                    : "Add accountant"}
                </Button>
              </div>
              <div className={item}>
                <div>
                  <h3>Third-party admin</h3>
                  <p>
                    This is someone based in the US who needs admin access to
                    your company—like an HR consultant or legal advisor—but
                    isn’t paid through Justworks. We’ll add them to your company
                    directory, but they won’t count toward your seat fees.
                  </p>
                </div>
                <Button
                  as="a"
                  href={buildWebPath(
                    "/invitation?invitation_member_type=vendor"
                  )}
                  variant="outlined"
                >
                  Add third-party
                </Button>
              </div>
            </>
          ) : (
            <div className={item}>
              <div>
                <h3>3rd-party admin or accountant</h3>
                <p>
                  Someone who will have access to Justworks or be included in
                  your company's directory, but is not paid through Justworks.
                </p>
              </div>
              <Button
                as="a"
                href={buildWebPath("/invitation?invitation_member_type=vendor")}
                variant="outlined"
              >
                Add 3rd-party
              </Button>
            </div>
          )}
          {!isInternationalStandalone && (
            <div className={item}>
              <div>
                <h3>Temp</h3>
                <p>
                  Someone who is contracted through an external agency and paid
                  through the third-party employment agency not Justworks.
                </p>
              </div>
              <Button
                as="a"
                href={buildWebPath("/invitation?invitation_member_type=temp")}
                variant="outlined"
              >
                Add temp
              </Button>
            </div>
          )}
        </Collapsible>
        <Tools />
      </div>
    </div>
  );
}
